import { missionPositionEntityName } from '../../../../backend/config/classifiers';
import { useMissionsTranslation } from '../../../../ui/hooks';
import { convertBooleanToYesOrNo } from '../../../../ui/utils/convertBooleanToYesOrNo';
import { getClassifierValues } from '../../../../ui/utils/getClassifierValue';
import { occupationEntityName, occupationGroupEntityName } from '@juvare/credentialing/backend/config/shared';
import { occupationEcls } from '@juvare/credentialing/backend/constants';
import { isTrainFeatureEnabled } from '@juvare/credentialing/ui/utils';
import { ClassifierAdminPage, ClassifierValue, SlideoutFields } from '@juvare/platform-sdk-react/ui/features/classifiers';
import { StateClassifierValue } from '@juvare/platform-sdk-react/ui/features/default-classifiers';
import { getPublicSettings, getSettings } from '@juvare/platform-sdk-react/ui/features/editable-settings';
import { capitalize } from '@mui/material';
import React from 'react';
import { useSelector } from 'react-redux';
import { getEclLabelByValue, mapCsvRowToData, mapDataToCsvRow, validateQualificationsCriteria } from './utils';
const EntityDetailsComponent = (props) => {
    var _a, _b;
    const userState = useSelector((state) => { var _a, _b; return (_b = (_a = state.platform.user) === null || _a === void 0 ? void 0 : _a.data) === null || _b === void 0 ? void 0 : _b.state; });
    const settings = useSelector(userState ? getSettings : getPublicSettings);
    const { t } = useMissionsTranslation();
    const { status, groupIds, occupationIds, description, ecl, skillIds, trainingIds, trainCourseIds, languageIds, state, tribeIds, counties, deploymentDurationId, deploymentTravelWillingnessIds, federalDeploymentWillingness, priorCommitments, residenceType, allOccupations, } = props.response.data;
    return (React.createElement(SlideoutFields, { fields: [
            { label: t('adminPages.status'), value: capitalize(status) },
            {
                label: t('adminPages.occupationGroup'),
                value: getClassifierValues(groupIds, occupationGroupEntityName),
            },
            allOccupations
                ? { label: t('adminPages.allOccupations'), value: convertBooleanToYesOrNo(allOccupations, t) }
                : {
                    label: t('adminPages.occupations'),
                    value: getClassifierValues(occupationIds, occupationEntityName),
                },
            { label: t('adminPages.description'), value: description ? description : undefined },
            {
                label: t('adminPages.ecl'),
                value: ecl === null || ecl === void 0 ? void 0 : ecl.reduce((previousValue, currentValue, i, { length }) => previousValue + getEclLabelByValue(currentValue) + (length - 1 !== i ? ', ' : ''), ''),
            },
            {
                label: t('adminPages.skills'),
                value: getClassifierValues(skillIds, 'skill'),
            },
            {
                label: t('adminPages.trainingCourses'),
                value: getClassifierValues(trainingIds, 'training'),
            },
            {
                label: t('adminPages.trainCourses', { esarVhpCode: (_b = (_a = settings.esarVhp) === null || _a === void 0 ? void 0 : _a.clientCode) !== null && _b !== void 0 ? _b : '' }),
                value: getClassifierValues(trainCourseIds, 'trainCourse'),
            },
            {
                label: t('adminPages.languages'),
                value: getClassifierValues(languageIds, 'language'),
            },
            {
                label: t('adminPages.state'),
                value: state ? React.createElement(StateClassifierValue, { state: state }) : undefined,
            },
            {
                label: t('adminPages.tribe'),
                value: residenceType === 'tribe' && tribeIds ? getClassifierValues(tribeIds, 'tribe') : undefined,
            },
            {
                label: t('adminPages.county'),
                value: residenceType === 'county' && state && counties ? counties.join(', ') : undefined,
            },
            {
                label: t('adminPages.willingToDeployFor'),
                value: React.createElement(ClassifierValue, { entity: 'deploymentDuration', id: deploymentDurationId }),
            },
            {
                label: t('adminPages.willingToTravel'),
                value: getClassifierValues(deploymentTravelWillingnessIds, 'deploymentTravelWillingness'),
            },
            {
                label: t('adminPages.federalDeploymentWillingness'),
                value: federalDeploymentWillingness !== undefined ? convertBooleanToYesOrNo(federalDeploymentWillingness, t) : undefined,
            },
            {
                label: t('adminPages.priorCommitments'),
                value: priorCommitments !== undefined ? convertBooleanToYesOrNo(priorCommitments, t) : undefined,
            },
        ] }));
};
function getEntityTitle(row) {
    return row.columns.name;
}
export const missionPositionFormConfig = (t, settings, userState, isEdit) => {
    var _a, _b;
    return ({
        key: '!form',
        type: 'form',
        children: [
            {
                type: 'form.text',
                key: 'name',
                label: t('adminPages.name'),
                validate: {
                    required: true,
                },
            },
            {
                type: 'form.select',
                key: 'status',
                label: t('adminPages.status'),
                validate: {
                    required: true,
                },
                values: [
                    {
                        value: 'active',
                        label: t('adminPages.statusLabels.active'),
                    },
                    {
                        value: 'deprecated',
                        label: t('adminPages.statusLabels.deprecated'),
                    },
                    {
                        value: 'retired',
                        label: t('adminPages.statusLabels.retired'),
                    },
                ],
            },
            {
                type: 'form.multiSelect',
                key: 'groupIds',
                label: t('adminPages.occupationGroup'),
                validate: {
                    custom: (value, handler) => validateQualificationsCriteria(handler, userState),
                },
                valueProvider: {
                    type: 'classifier.occupationGroup',
                },
            },
            {
                type: 'form.multiSelect',
                key: 'occupationIds',
                label: t('adminPages.occupations'),
                selectAll: true,
                limitTags: 5,
                validate: {
                    custom: (value, handler) => {
                        var _a, _b;
                        const allOccupationsData = (_a = handler.up().down('allOccupations')) === null || _a === void 0 ? void 0 : _a.getData();
                        const occupationGroupData = (_b = handler.up().down('groupIds')) === null || _b === void 0 ? void 0 : _b.getData();
                        // Disable occupation selection if no occupation group or "all occupations" is selected
                        handler.setDisabled(!(occupationGroupData === null || occupationGroupData === void 0 ? void 0 : occupationGroupData.length) || allOccupationsData);
                        // Clear data if "all occupations" is selected or no occupation groups are present
                        if (allOccupationsData || !(occupationGroupData === null || occupationGroupData === void 0 ? void 0 : occupationGroupData.length)) {
                            handler.setData(undefined);
                        }
                        // Show error if occupation group is selected, but neither "all occupations", nor any occupation is selected
                        if (!!occupationGroupData.length && !(value === null || value === void 0 ? void 0 : value.length) && !allOccupationsData) {
                            return 'Occupations are missing';
                        }
                        return undefined;
                    },
                },
                valueProvider: {
                    type: 'classifier.occupationsByGroupIds',
                    paramsFrom: '^groupIds',
                },
            },
            {
                type: 'form.checkbox',
                key: 'allOccupations',
                label: 'All Occupations',
                validate: {
                    custom: (value, handler) => {
                        var _a, _b;
                        const occupationGroupData = (_a = handler.up().down('groupIds')) === null || _a === void 0 ? void 0 : _a.getData();
                        // Disable "all occupations" checkbox if no occupation groups are present
                        handler.setDisabled(!(occupationGroupData === null || occupationGroupData === void 0 ? void 0 : occupationGroupData.length));
                        // Clear data if no occupation groups are present
                        if (!(occupationGroupData === null || occupationGroupData === void 0 ? void 0 : occupationGroupData.length)) {
                            handler.setData(undefined);
                        }
                        const occupationsData = (_b = handler.up().down('occupationIds')) === null || _b === void 0 ? void 0 : _b.getData();
                        // Show error if occupation group is selected, but neither "all occupations", nor any occupation is selected
                        if (!!occupationGroupData.length && !value && !(occupationsData === null || occupationsData === void 0 ? void 0 : occupationsData.length)) {
                            return 'Occupations are missing';
                        }
                        return undefined;
                    },
                },
            },
            {
                type: 'form.text',
                key: 'description',
                label: t('adminPages.description'),
                multiline: true,
                rows: 5,
            },
            {
                type: 'form.multiSelect',
                key: 'ecl',
                label: t('adminPages.ecl'),
                values: occupationEcls,
                selectAll: true,
            },
            {
                type: 'form.multiSelect',
                key: 'skillIds',
                label: t('adminPages.skillsAllOfTheFollowing'),
                valueProvider: {
                    type: 'classifier.skill',
                },
                validate: { custom: (value, handler) => validateQualificationsCriteria(handler, userState) },
            },
            {
                type: 'form.multiSelect',
                key: 'trainingIds',
                label: t('adminPages.trainingCoursesAllOfTheFollowing'),
                valueProvider: {
                    type: 'classifier.training',
                },
                validate: { custom: (value, handler) => validateQualificationsCriteria(handler, userState) },
            },
            ...(isTrainFeatureEnabled(userState)
                ? [
                    {
                        type: 'form.multiSelect',
                        key: 'trainCourseIds',
                        label: t('adminPages.trainCoursesAllOfTheFollowing', { esarVhpCode: (_b = (_a = settings.esarVhp) === null || _a === void 0 ? void 0 : _a.clientCode) !== null && _b !== void 0 ? _b : '' }),
                        valueProvider: { type: 'classifier.trainCourse' },
                        validate: { custom: (_value, handler) => validateQualificationsCriteria(handler, userState) },
                    },
                ]
                : []),
            {
                type: 'form.multiSelect',
                key: 'languageIds',
                label: t('adminPages.languagesAllOfTheFollowing'),
                valueProvider: {
                    type: 'classifier.language',
                },
                validate: { custom: (value, handler) => validateQualificationsCriteria(handler, userState) },
            },
            {
                type: 'form.select',
                key: 'state',
                label: t('adminPages.state'),
                valueProvider: {
                    type: 'classifier.state',
                },
            },
            {
                type: 'form.panel',
                children: [
                    {
                        type: 'form.radio',
                        key: 'residenceType',
                        label: t('adminPages.countyTribe'),
                        values: [
                            {
                                value: 'county',
                                label: t('adminPages.county'),
                            },
                            {
                                value: 'tribe',
                                label: t('adminPages.tribe'),
                            },
                        ],
                        style: {
                            inline: true,
                            width: 250,
                        },
                    },
                    {
                        type: 'form.multiSelect',
                        key: 'counties',
                        label: t('adminPages.county'),
                        valueProvider: {
                            type: 'classifier.county',
                            paramsFrom: '^^state',
                        },
                        hidden: {
                            when: '^residenceType',
                            dataNotEq: 'county',
                        },
                        clearDataOnHide: true,
                    },
                    {
                        type: 'form.multiSelect',
                        key: 'tribeIds',
                        label: t('adminPages.tribe'),
                        valueProvider: {
                            type: 'classifier.tribe',
                        },
                        hidden: {
                            when: '^residenceType',
                            dataNotEq: 'tribe',
                        },
                        clearDataOnHide: true,
                    },
                ],
                style: {
                    layout: 'row',
                    horizontalAlign: 'left',
                },
            },
            {
                type: 'form.select',
                key: 'deploymentDurationId',
                label: t('adminPages.deploymentDuration'),
                valueProvider: {
                    type: 'classifier.deploymentDuration',
                },
            },
            {
                type: 'form.multiSelect',
                key: 'deploymentTravelWillingnessIds',
                label: t('adminPages.willingToTravel'),
                selectAll: true,
                valueProvider: {
                    type: 'classifier.deploymentTravelWillingness',
                },
            },
            {
                type: 'form.yesNo',
                key: 'federalDeploymentWillingness',
                label: t('adminPages.federalDeploymentWillingness'),
                showNotSet: true,
                style: {
                    inline: true,
                },
            },
            {
                type: 'form.yesNo',
                key: 'priorCommitments',
                label: t('adminPages.priorCommitments'),
                showNotSet: true,
                style: {
                    inline: true,
                },
            },
            ...(isEdit
                ? [
                    {
                        type: 'form.content',
                        text: t('adminPages.editWarning'),
                        style: {
                            alert: 'warning',
                        },
                    },
                ]
                : []),
        ],
    });
};
export const MissionPositionAdminPage = () => {
    var _a, _b;
    const userState = useSelector((state) => { var _a, _b; return (_b = (_a = state.platform.user) === null || _a === void 0 ? void 0 : _a.data) === null || _b === void 0 ? void 0 : _b.state; });
    const settings = useSelector(userState ? getSettings : getPublicSettings);
    const { t } = useMissionsTranslation();
    const csvFileHeader = [
        t('adminPages.id'),
        t('adminPages.name'),
        t('adminPages.status'),
        t('adminPages.occupationGroup'),
        t('adminPages.occupations'),
        t('adminPages.allOccupations'),
        t('adminPages.description'),
        t('adminPages.ecl'),
        t('adminPages.skillsAllOfTheFollowing'),
        t('adminPages.trainingCoursesAllOfTheFollowing'),
        ...(isTrainFeatureEnabled(userState)
            ? [t('adminPages.trainCoursesAllOfTheFollowing', { esarVhpCode: (_b = (_a = settings.esarVhp) === null || _a === void 0 ? void 0 : _a.clientCode) !== null && _b !== void 0 ? _b : '' })]
            : []),
        t('adminPages.languagesAllOfTheFollowing'),
        t('adminPages.state'),
        t('adminPages.counties'),
        t('adminPages.tribes'),
        t('adminPages.deploymentDuration'),
        t('adminPages.willingToTravel'),
        t('adminPages.federalDeploymentWillingness'),
        t('adminPages.priorCommitments'),
    ];
    return (React.createElement(ClassifierAdminPage, { entity: missionPositionEntityName, title: 'Mission Position', fullTextSearchField: 'search', getEntityTitle: getEntityTitle, entityDetailsComponent: EntityDetailsComponent, formConfig: missionPositionFormConfig(t, settings, userState, true), csvFileHeader: csvFileHeader, mapCsvRowToData: (row) => mapCsvRowToData(row, isTrainFeatureEnabled(userState)), mapDataToCsvRow: (response) => mapDataToCsvRow(response, isTrainFeatureEnabled(userState)) }));
};
